import { useCallback, useRef, useState, type MutableRefObject } from 'react';
import { useLazyGetUserConsentsQuery, type UserConsentItem } from '../../../redux/apis/consents.api';

const findAiConsent = (consents?: UserConsentItem[]) =>
  consents?.find((consent) => consent.consentKey?.toUpperCase().includes('AI'));

export type UseModalOptions = {
  onAccepted: () => void;
};

export type UseModalResult = {
  open: boolean;
  setOpen: (open: boolean) => void;
  openModal: () => void;
  closeModal: () => void;
  handleAiButtonClick: () => void;
  pendingAiConsentRef: MutableRefObject<UserConsentItem | null>;
};

export function useModal({ onAccepted }: UseModalOptions): UseModalResult {
  const [open, setOpen] = useState(false);
  const pendingAiConsentRef = useRef<UserConsentItem | null>(null);

  const [getUserConsents] = useLazyGetUserConsentsQuery();

  const openModal = useCallback(() => setOpen(true), []);
  const closeModal = useCallback(() => setOpen(false), []);

  const handleAiButtonClick = useCallback(() => {
    getUserConsents()
      .unwrap()
      .then((response) => {
        const aiConsent = findAiConsent(response?.consents);

        if (aiConsent?.status === 'ACCEPTED') {
          onAccepted();
          return;
        }

        pendingAiConsentRef.current = aiConsent ?? null;
        openModal();
      })
      .catch((err) => {
        console.error('Failed to fetch user consents:', err);
        pendingAiConsentRef.current = null;
        openModal();
      });
  }, [getUserConsents, onAccepted, openModal]);

  return {
    open,
    setOpen,
    openModal,
    closeModal,
    handleAiButtonClick,
    pendingAiConsentRef,
  };
}
